/**
 * 파일명 : cod0103m.tsx
 * 설명 : 달력 관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-04-27   이경원     최초작성
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React, { useReducer, useEffect } from "react";

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Cod0103mAction  } from '../../../redux/slice/cod/cod0103m_slice';
import { IHolidayState, IInquiryCndsState} from '../../../redux/models/cod/cod0103m_model';

/*  AntD  */
import { Badge, Button, Row, Col, Layout, Space, Calendar, Typography,  Table, Modal, Input , Select,DatePicker } from "antd";
import {  PlusOutlined, EditOutlined } from "@ant-design/icons";

/*  업무  */
import * as CommonProxy from 'libs/CommonProxy';
import * as common      from 'libs/common';
import dayjs, { Dayjs } from 'dayjs';
import { log } from "console";

/************************************************
 * 전역변수 영역
 ************************************************/
const Cod0103m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography; 
    
    /* Table Columns */
    const holidaysColumns = React.useCallback(() => {
        const arr: any = [
            {
                title    : '일자',
                dataIndex: 'baseDt',
                key      : 'baseDt',
                width    : '40%',
                align    : 'center',
            },
            {
                title    : '명칭',
                dataIndex: 'dateNm',
                key      : 'dateNm',
                width    : '60%',
            },
        ];
        return arr;
    }, []);
    
	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const { originHolidays 
           ,modifiedHolidays
           ,apiHolidays 
           ,thisYearGen 
           ,nextYearGen
           ,generatedYears
           ,inquiryCnds }                 = useAppSelector((state) => state.Cod0103m);
    const { searchYear    , searchMonth } = useAppSelector((state) => state.Cod0103m.inquiryCnds);
    const [selectedRows   , setSelectedRows]    = React.useState<IHolidayState[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
const [isManualModalOpen, setIsManualModalOpen] = React.useState(false);

const [manualDate, setManualDate] = React.useState<Dayjs | null>(null);
const [manualName, setManualName] = React.useState('');
const [selectedHoliday, setSelectedHoliday] = React.useState<IHolidayState | null>(null);


    //수정된 공휴일 Map 변환
    const modifiedMap = React.useMemo(() => {
    return modifiedHolidays.reduce((acc, cur) => {
        acc[cur.baseDt] = cur;
        return acc;
    }, {} as Record<string, any>);
    }, [modifiedHolidays]); 

    //선택된 월에 해당하는 공공 API 필터링
    const filteredApiHolidays = React.useMemo(() => {
       return apiHolidays.filter((item: any) => {
            const baseDt = item.baseDt; // YYYYMMDD
            const year   = Number(baseDt?.substring(0, 4));
            const month  = Number(baseDt?.substring(4, 6));

            return year === searchYear && month === searchMonth;
        });
    }, [apiHolidays, searchYear, searchMonth]); 

    let label    = '';
   
    /**
    * 기능    : 화면 최초 로드시에 필요한 기능을 구현한다.
    * param  : 
    * return : None
    */
    React.useEffect(() => {
        const now = dayjs();
        //올해,이번달로 초기화
        dispatch( Cod0103mAction.setInquiryCnds({ searchYear: now.year(), searchMonth: now.month() + 1 }));
        //선택날짜 초기화
        setSelectedHoliday(null);
        //공휴일조회 (DB저장된 공휴일/API공휴일/생성된달력년도리스트)
        fn_GetCalendarHolidays();
    }, []);

    // 년도 바뀔 때 재조회
    React.useEffect(() => {
        // 처음 렌더링 시 inquiryCnds가 비어있을 수 있으니 체크
        if (inquiryCnds.searchYear) {
            fn_GetCalendarHolidays();
        }
    }, [inquiryCnds.searchYear]);

    /**
     * 기능   : 해당년도 공휴일 조회
     * param : searchYear, searchMonth
     * return : 해당년도 DB등록된 공휴일, 해당년도 월별 공공 API 
     */ 
    const fn_GetCalendarHolidays = () => {
        //초기화
        dispatch(Cod0103mAction.setOriginHolidays([]));
        dispatch(Cod0103mAction.clearModifiedHolidays());
        dispatch(Cod0103mAction.setApiHolidays([]));

        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
        };

        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0031',
            success: (responseData: any) => {
                const data = responseData;
                console.log("responseData >>>", responseData);
                // 1. DB 저장 공휴일
                dispatch( Cod0103mAction.setOriginHolidays( (data.holidayList || []).filter((v: any) => v != null)));

                // 2. 공공 API 공휴일
                dispatch( Cod0103mAction.setApiHolidays ((data.apiHolidayList || []).filter((v: any) => v != null)));

                // 3. 달력 상태
                dispatch( Cod0103mAction.setCalendarStatus({
                            thisYearGen   : data.thisYearGen,
                            nextYearGen   : data.nextYearGen,
                            generatedYears: data.calYearsList || [],
                        })
                );
            },
        });
    };    

    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능    : onClick 이벤트 선언
     * 설명    : 공통 클릭 이벤트 핸들러
     * param  : 
     * return : None
     */
    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const buttonName = e.currentTarget.name;

        switch (buttonName) {
            case 'btnCalCre'  :    //달력생성
                console.log("달력생성")
                //달력생성
                fn_CreateCalendar();
                break;

            case 'btnAdd'  :       //달력에 추가
                console.log("달력추가")
                //달력생성
                onAddHolidays();
                break;

            case 'btnManualAdd':   //개별휴일등록
                console.log("개별등록") 
                if (!calendarState.showCalendar) {
                    common.openInfo({
                        contents: '해당년도 달력이 생성되지 않아 공휴일 등록이 불가능합니다. 달력생성 후 재시도해주세요',
                    });
                    setSelectedRowKeys([]); // 선택 비우기
                    return;
                }
                setIsManualModalOpen(true);
                break;

            case 'btnSave':       //저장
                console.log("저장") 
                //달력저장
                fn_SaveCalendar();
                break;
                
            case 'btnDelete':     //삭제
                console.log("삭제") 
                //달력삭제
                onDeleteHolidays(selectedHoliday);
                break;
                    
            default:
        }
    };

    
    /**
     * 기능   : onAddHolidays 이벤트 선언
     * param : 
     */
    const onAddHolidays = () => {
        if (!calendarState.showCalendar) {
            common.openInfo({
                contents: '해당년도 달력이 생성되지 않아 공휴일 등록이 불가능합니다. 달력생성 후 재시도해주세요',
            });
            return;
        }
        confirmOverwriteHolidays(selectedRows, () => {
            selectedRows.forEach((row) => {dispatch(Cod0103mAction.addHoliday(row));});
        });
    };

    
    const onDeleteHolidays = (target: IHolidayState | null) => {

        if (!calendarState.showCalendar) {
            common.openInfo({
                contents: '해당년도 달력이 생성되지 않아 공휴일 등록이 불가능합니다. 달력생성 후 재시도해주세요',
            });
            return;
        }

        if (!target) {
            common.openInfo({ contents: '삭제할 날짜를 선택해주세요.' });
            return;
        }

        const baseDt = target.baseDt;
        const isOrigin = originHolidays.some(v => v.baseDt === baseDt);

        // 1.영업일로 변경
        if (isOrigin) {
            dispatch(Cod0103mAction.updateHoliday({
                ...target,
                dateNm: '영업일',
                dateDcd: '00',
                holidayYn: 'N',
                dirty: true,
            }));
            return;
        }

        // 2.수정또는신규데이터는 그냥 삭제
        dispatch(Cod0103mAction.removeModifiedHoliday(baseDt));
};

    /************************************************
     * 개발자함수 영역
     ************************************************/
    /**
     * 기능   : 문구/[달력생성]버튼 제어 
     * param : 
     */
    const getCalendarState = () => {
        const year         = searchYear;
        const thisYear     = new Date().getFullYear();
        const nextYear     = thisYear + 1;
        const hasGenerated = generatedYears?.includes(String(year));

        let showMessage    = false;
        let enableCreate   = false;
        let showCalendar   = false;

         // 1. 기본 캘린더 여부
        if (year === thisYear && thisYearGen) {
            showCalendar = true;
        }

        if (year === nextYear && nextYearGen) {
            showCalendar = true;
        }

        if (hasGenerated) {
            showCalendar = true;
        }

         //올해 선택
        if (year === thisYear) {

            // 올해 미생성
            if (!thisYearGen) {
                label        = '올해';
                showMessage  = true;
                enableCreate = true;
            }

            // 올해 생성 & 내년 미생성
            if (thisYearGen && !nextYearGen) {
                label       = '내년'; 
                showMessage = true;
                enableCreate = false;
            }
        }
        //내년 선택
        else if (year === nextYear) {
            if (!nextYearGen) {
                label        = `${year}년`;
                showMessage  = true;
                enableCreate = true;
            }
        }
        //그 외 미생성
        else if (!hasGenerated) {
            label        = `${year}년`;
            showMessage  = true;
            enableCreate = true;
        }

        return {
            label,
            showMessage,
            enableCreate,
            showCalendar
        };
    };

    const calendarState = getCalendarState();

    /**
     * 기능   : [달력생성]버튼 클릭시 호출
     */
    const fn_CreateCalendar = () => {
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
        };

        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMS0025',
            success: (responseData: any) => {
                // 서버에서 rtnDto.setData(response)로 보낸 데이터 추출
                const { result, message } = responseData;

                
               if (result === 'S') {
                    dispatch(Cod0103mAction.setInquiryCnds({ searchYear: inquiryCnds.searchYear,searchMonth: inquiryCnds.searchMonth}));

                    //재조회
                    fn_GetCalendarHolidays();
                } else {
                    // 서버에서 result: "F"로 내려온 경우 (비즈니스 로직 에러)
                    common.openError({ 
                        contents: message || '[달력생성]버튼을 눌러 재생성해주세요.',
                    });
                }
            },
            error: () => {
                const param = {
                    contents: '달력생성에 실패하였습니다.[달력생성]버튼을 눌러 재생성해주세요.',
                };
                common.openError(param);
                return;
            }
        });
        
    };    

    /**
     * 기능   : 등록/수정시 중복체크
     */
    const confirmOverwriteHolidays = (rows: IHolidayState[], onConfirm: () => void) => {

        const exists = rows.some((row) => {
            const baseDt = row.baseDt;

            const isInOrigin   = originHolidays.some(h => h.baseDt === baseDt);
            const isInModified = modifiedHolidays.some(h => h.baseDt === baseDt);

            return isInOrigin || isInModified;
        });

        if (!exists) {
            onConfirm();
            return;
        }

        const param = {
            title: '위드정보 확인',
            contents: '이미 등록된 공휴일이 있습니다. 덮어쓰시겠습니까?',
            accept: () => {
                onConfirm();
            }
        };

        common.openConfirm(param);
    };

    /**
     * 기능   : 캘린더 이동(달 변경)
     */
    const moveCalendar = (nextYear: number, nextMonth: number, selectedData?: any) => {
        const hasDirty = modifiedHolidays.some(v => v.dirty);

        const move = () => {
            dispatch(Cod0103mAction.clearModifiedHolidays());
            
            setSelectedRows([]);
            setSelectedRowKeys([]);

            if (selectedData) {
                setSelectedHoliday(selectedData);
            }

            dispatch(Cod0103mAction.setInquiryCnds({
                searchYear: nextYear,
                searchMonth: nextMonth,
            }));
        };

        if (hasDirty) {
            common.openConfirm({
                title: '위드정보 확인',
                contents: '저장하지 않은 변경사항이 있습니다. 이동하시겠습니까?',
                accept: move
            });
        } else {
            move();
        }
    };

    /**
     * 기능   : [저장]버튼 클릭시 호출
     */
    const fn_SaveCalendar = () => {
    
        if (!calendarState.showCalendar) {
            common.openInfo({
                contents: '해당년도 달력이 생성되지 않아 공휴일 등록이 불가능합니다. 달력생성 후 재시도해주세요',
            });
            return;
        }

        //신규 또는 수정 항목 체크
        if (!modifiedHolidays || modifiedHolidays.length === 0) {
            common.openInfo({ contents: '저장할 내역이 없습니다.',});
            return;
        } else {
            const param = {
                title   : '위드정보 확인',
                contents: '저장하시겠습니까?',
                accept: () => {
                    
                    //입력파라미터
                    const requestData = {
                        holidayList: modifiedHolidays.filter(v => v.dirty) 
                                                    .map(v => ({
                                                        baseDt: v.baseDt,
                                                        dateNm: v.dateNm,
                                                        dateDcd: v.dateDcd,
                                                        holidayYn: v.holidayYn,
                                                     }))
                                                    //  .map(({ dirty, ...rest }) => rest)
                    };
            
                    CommonProxy.callAsyncSvc(requestData, {
                        txCd : 'TRCOMS0026',
                        success : (responseData:any) => {
                            const param = {
                                title : '위드정보 확인',
                                contents: '저장 완료 되었습니다.',
                                accept: (data: any) => {
                                    //DB에 저장된 공휴일만 갱신 
                                    dispatch(Cod0103mAction.setOriginHolidays(responseData.holidayList)) ;
                                    //화면상 수정된 데이터 초기화 
                                    dispatch(Cod0103mAction.clearModifiedHolidays());
                                }
                            };
                            common.openAlert(param);

                        },
                    });
                }
            };
            common.openConfirm(param);
        }
    };    

    /**
     * 기능   : 모달창에서 [등록] 버튼 클릭 시 호출
     */
    const onRegisterManualHoliday = () => {
        if (!manualDate || !manualName.trim()) {
            common.openInfo({ contents: '기준일자와 휴일명을 입력해주세요.',});
            return;
        }

        const newHoliday: IHolidayState = {
            baseDt: manualDate.format('YYYYMMDD'),
            dateNm: manualName,
            holidayYn: 'Y',
            dateDcd: '03',
            dirty: true,
        };
        const targetYear  = Number(newHoliday.baseDt.substring(0, 4));
        const targetMonth = Number(newHoliday.baseDt.substring(4, 6));
        
        const isSameMonth = targetYear === searchYear && targetMonth === searchMonth;
  
        const moveAndSave = () => {
            // 새 휴일추가 
            dispatch(Cod0103mAction.addHoliday(newHoliday));
            //조회조건 업데이트
            dispatch(Cod0103mAction.setInquiryCnds({ searchYear: targetYear,searchMonth: targetMonth,}));
            setSelectedHoliday(newHoliday);
            resetModal();
        };

        //선택된달과 같은 달
        if (isSameMonth) {
            confirmOverwriteHolidays([newHoliday], () => { moveAndSave(); });
            return;
        }
        //선택된달과 다른 달
        if (hasDirtyInCurrentMonth()) {
            common.openConfirm({
                title   : '위드정보 확인',
                contents: '저장하지 않은 변경사항이 있습니다. 이동하시겠습니까?',
                accept  : () => {
                    dispatch(Cod0103mAction.clearModifiedHolidays());
                    // 이동 후 중복 체크
                    confirmOverwriteHolidays([newHoliday], () => { moveAndSave(); });
                }
            });
            return;
        }
        //중복 체크
        confirmOverwriteHolidays([newHoliday], () => { moveAndSave(); });
    };

    /**
     * 기능   : 모달창에서 [등록] 버튼 클릭 시 호출
     */
    const hasDirtyInCurrentMonth = () => {
        return modifiedHolidays.some(v => {
            const year  = Number(v.baseDt.substring(0, 4));
            const month = Number(v.baseDt.substring(4, 6));

            return v.dirty && year === searchYear&& month === searchMonth;
        });
    };
    /**
     * 기능   : 모달창초기화
     */
    const resetModal = () => {
        setManualDate(null);
        setManualName('');
        setIsManualModalOpen(false);
    };  

    
    return (
        /* 화면 디자인 영역 */
        <Layout>
        <Content>
            {/* 상단 */}
            <Row style={{ height: 'calc(100vh - 180px)' }}>
                {/* 좌측: 달력 영역 (약 65~70%) */}
                <Col style={{ 
                    width        : '70%', 
                    padding      : 20,
                    display      : 'flex',
                    flexDirection: 'column'
                }}>
                    {/* 상단 메시지 + 버튼 */}
                    <Row  justify="space-between"  align="middle">
                        <Space>
                            {calendarState.showMessage && (
                            <Text   style={{fontWeight: 500, color: '#ff4d4f' }}>※{calendarState.label} 달력이 생성되지 않았습니다. [달력생성]버튼을 클릭해 달력을 생성해주세요.</Text>
                            )}
                        </Space>

                        <Space>
                            <Button name='btnCalCre' size="middle" onClick={onClick} className='background-color-primary-button' disabled={!calendarState.enableCreate} icon={<EditOutlined/> }>달력생성</Button>
                        </Space>
                    </Row>
                    
                    {/* 달력 영역 */}
                    <div style={{ 
                        marginTop   : 5, 
                        flex        : 1,
                        overflow    : 'hidden',
                        // border      : '1px solid #d9d9d9', 
                        // borderRadius: '8px', 
                        // background  : '#fff', 
                        padding     : '10px'
                    }}>
                        <Calendar 
                            fullscreen  = {true}
                            value={selectedHoliday?.baseDt ? dayjs(selectedHoliday.baseDt) : dayjs()}
                            headerRender= {() => {
                                return (
                                    <div
                                        style={{
                                            display       : 'flex',
                                            justifyContent: 'center', 
                                            alignItems    : 'center',
                                            padding       : 0,
                                            marginBottom  : '20px'
                                        }}
                                    >
                                        <Space>
                                            <Select
                                                className="year-month-select"
                                                size="large"
                                                value={searchYear}
                                                style={{ width: "fit-content" }}
                                                variant="borderless"
                                                onChange={(y) => {
                                                    moveCalendar(y, searchMonth)    
                                                }}
                                                options={Array.from({ length: 21 }).map((_, i) => {
                                                    const currentYear = new Date().getFullYear();
                                                    const year = currentYear - 10 + i;

                                                    return {
                                                        value: year,
                                                        label: `${year} 년`,
                                                    };
                                                })}
                                            />

                                            <Select
                                                className="year-month-select"
                                                size="large"
                                                value={searchMonth}
                                                style={{ width: "fit-content" }}
                                                variant="borderless"
                                                onChange={(m) => {
                                                    moveCalendar(searchYear, m)      
                                                }}
                                                options={Array.from({ length: 12 }).map((_, i) => ({
                                                    value: i + 1,
                                                    label: `${i + 1} 월`
                                                }))}
                                            />
                                        </Space>
                                    </div>
                                );
                            }}
                            cellRender   = {(current, info) => {
                                if (info.type === 'date') {
                                    const dateStr       = current.format('YYYYMMDD');
                                    //공휴일데이터 찾기(수정된 데이터있으면 그거 쓰고 아니면 원본데이터)
                                    const holiday       = modifiedMap[dateStr] || originHolidays.find(h => h.baseDt === dateStr);
                                    const isHoliday     = holiday?.holidayYn === 'Y';   //휴일여부
                                    const isHighlighted = holiday?.dirty     === true;  //변경여부
                                    const isSelected = selectedHoliday?.baseDt === dateStr;

                                    return (
                                        <div 
                                            // style={{
                                            //     backgroundColor: isSelected ? '#bae7ff' : isHighlighted ? '#fffbe6' : undefined,
                                            //     padding        : 4,
                                            //     borderRadius   : 4,
                                            //     height         : '100%'
                                            // }}
                                            className={`${isHighlighted ? 'dirty' : ''} ${isSelected ? 'is-selected' : ''}`}
                                        >
                                            {/* 공휴일일 때만 뱃지 */}
                                            {isHoliday&& (
                                                <div style={{ marginTop: 4 }}>
                                                    <Badge status="error" text={holiday.dateNm} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            }}
                            onSelect={(date, info) => {
                                const nextYear  = date.year();
                                const nextMonth = date.month() + 1;
                                const dateStr  = date.format('YYYYMMDD');
                                const selected = 
                                    modifiedMap[dateStr] ||
                                    originHolidays.find(h => h.baseDt === dateStr) || {
                                        baseDt: dateStr,
                                        dateNm: '',
                                        dateDcd: '',
                                        holidayYn: 'N',
                                        dirty: false,
                                    };

                                //달이 바뀌는 이동인지 확인
                                const isDifferentMonth =  nextYear !== inquiryCnds.searchYear || nextMonth !== inquiryCnds.searchMonth;

                                //월이 다른 경우, 기존 moveCalendar 함수에 위임
                                if (info.source === 'date' && isDifferentMonth) {
                                    moveCalendar(nextYear, nextMonth,selected);
                                    return; 
                                }

                                //같은 달내 이동 또는 이동 확정인경우
                                setSelectedHoliday(selected);
                            }}
                            onPanelChange= {(date, mode) => {
                                if (mode === "month") {
                                    moveCalendar(date.year(), date.month() + 1);
                                }
                            }}
                            
                        />
                    </div>
                </Col>

                {/* 우측: 공휴일목록 (약 30%) */}
                <Col style={{ 
                    width        : '20%', 
                    padding      : 20, 
                    display      : 'flex', 
                    flexDirection: 'column'
                }}>
                    {/* 상단 메시지 + 버튼 */}
                    <Row  justify="space-between"  align="middle" style={{ marginTop: 30 }}>
                        <Space>
                            <img className='title-icon' alt='' />
                             <Text strong>{searchMonth}월 공휴일 목록</Text>
                        </Space>

                        <Space>
                            <Button name='btnAdd' onClick={onClick} size="small" className='background-color-default-button' icon={<PlusOutlined />}>달력에 추가</Button>
                        </Space>
                    </Row>

                    {/*공휴일목록 체크박스 테이블*/}
                    <div style={{ 
                        marginTop    : 5, 
                        flex         : 1, 
                        overflowY    : 'auto',
                        display      : 'flex',
                        flexDirection: 'column',
                        border       : '1px solid #d9d9d9', 
                        borderRadius : '8px', 
                        background   : '#fff'
                    }}>

                        <div style={{ flex: 1, padding: '10px', overflow: 'hidden' }}>
                            <Table 
                                dataSource  = {filteredApiHolidays}
                                columns     = {holidaysColumns()} 
                                size        = 'small'
                                rowKey      = 'baseDt'
                                pagination  = {false} 
                                scroll      = {{ y:'100%' }}  // 높이 넘어가면 스크롤 생김
                                rowSelection= {{
                                    type: 'checkbox',
                                    selectedRowKeys,   
                                    onChange: (keys, rows) => {
                                        setSelectedRowKeys(keys);
                                        setSelectedRows(rows as IHolidayState[]);
                                    }
                                }}
                            />
                        </div>
                    </div>                    
                </Col>
            </Row>
            {/* 하단 */}
            <Row>
            <Col style={{ width: '70%'}} >
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: -10 }}>
                    <Button name='btnManualAdd'  onClick={onClick} className='background-color-default-button' style={{ marginRight: "8px" }}>개별휴일등록</Button>
                    <Button name='btnDelete'     onClick={onClick} className='background-color-default-button' style={{ marginRight: "8px" }}>휴일삭제</Button>
                    <Button name='btnSave'       onClick={onClick} className='background-color-primary-button'  style={{ marginRight: "8px" }}>저장</Button>
                </div>
            </Col>
            </Row>
            {/* 모달영역 */}
            <Modal
                title     ="개별 휴일 등록"
                open      ={isManualModalOpen}
                onCancel  ={resetModal}
                onOk      ={onRegisterManualHoliday}
                okText    ="등록"
                cancelText="닫기"
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    {/* 기준일자 */}
                    <div>
                        <Text>기준일자</Text>
                        <br />
                        <DatePicker
                            value   ={manualDate}
                            onChange={(d) => setManualDate(d)}
                            format  ="YYYY-MM-DD"
                        />
                    </div>

                    {/* 휴일명 */}
                    <div>
                        <Text>휴일명</Text>
                        <Input
                            value      ={manualName}
                            onChange   ={(e) => setManualName(e.target.value)}
                            placeholder="휴일명을 입력해주세요."
                        />
                    </div>
                </Space>
            </Modal>
        </Content>
    </Layout>
);
};

export default Cod0103m;
